/** @fileoverview A single KPI row with selection checkbox and source picker. */
import { memo } from "react";
import { parseTags } from "../Components/TagFilterUtils";
import Checkbox from "../../../globalComponents/ui/Checkbox";
import SourcePicker from "./SourcePicker";

/**
 * One selectable KPI row showing its tags and (optionally) a source picker.
 * @param {object} props Component props.
 * @param {object} props.kpi The KPI/question.
 * @param {boolean} props.checked Whether the KPI is selected.
 * @param {(checked:boolean)=>void} props.onChange Selection toggle handler.
 * @param {boolean} props.isLast Whether this is the last row (border handling).
 * @param {boolean} props.disabled Disable interaction.
 * @param {Array<{id:number,name:string}>} props.sources Available energy sources.
 * @param {Record<number, number>} props.kpiSourceMap Per-KPI source assignments.
 * @param {(kpiId:number, sourceId:number|null)=>void} props.onKpiSourceChange Assignment handler.
 * @param {boolean} props.showSourcePicker Whether to render the inline picker.
 * @returns {JSX.Element} The KPI row.
 */
function KpiListRow({ kpi, checked, onChange, isLast, disabled, sources, kpiSourceMap, onKpiSourceChange, showSourcePicker }) {
  const combos = parseTags(kpi.tags);
  const allTags = []; const seen = new Set();
  for (const combo of combos)
    for (const t of combo) {
      if (!t?.key || !t?.value) continue;
      const k = `${t.key}:::${t.value}`;
      if (!seen.has(k)) { seen.add(k); allTags.push(t); }
    }

  const assignedSourceId = kpiSourceMap[kpi.questionId] || null;

  return (
    <div style={{
      borderBottom: isLast ? "none" : "1px solid var(--es-slate-100)",
      background: checked ? "var(--es-sky-50)" : "transparent",
      transition: "background 0.1s"
    }}>
      <div
        onClick={() => { if (!disabled) onChange(!checked); }}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", cursor: disabled ? "default" : "pointer" }}
      >
        <Checkbox checked={checked} onChange={onChange} disabled={disabled} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: checked ? "var(--es-sky-900)" : "var(--es-slate-800)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={kpi.title}>
            {kpi.title || "—"}
          </div>
          {allTags.length > 0 && (
            <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
              {allTags.slice(0, 4).map((t, i) => (
                <span key={i} style={{ fontSize: 10, color: "var(--es-slate-600)", background: "var(--es-slate-100)", border: "1px solid var(--es-slate-200)", borderRadius: 4, padding: "1px 6px" }}>{t.value}</span>
              ))}
              {allTags.length > 4 && <span style={{ fontSize: 10, color: "var(--es-slate-400)" }}>+{allTags.length - 4}</span>}
            </div>
          )}
        </div>

        {showSourcePicker && (
          <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}>
            <SourcePicker
              sources={sources}
              value={assignedSourceId}
              onChange={id => onKpiSourceChange(kpi.questionId, id)}
              compact
              disabled={disabled || !checked}
            />
          </div>
        )}

        {!showSourcePicker && assignedSourceId && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: "var(--es-amber-50)", color: "var(--es-source)", border: "1px solid var(--es-amber-300)", flexShrink: 0 }}>
            ⚡ Source#{assignedSourceId}
          </span>
        )}

        {checked && <span style={{ fontSize: 9, fontWeight: 700, color: "var(--es-cyan-700)", background: "var(--es-sky-100)", borderRadius: 4, padding: "2px 6px", flexShrink: 0 }}>SELECTED</span>}
      </div>
    </div>
  );
}

export default memo(KpiListRow);
